import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, Loader2, Repeat2, Sparkles } from "lucide-react";
import { DonorBadge, DONOR_TIERS } from "../components/DonorBadge";
import { toast } from "sonner";

const TIER_ROWS = [
  { tier: "supporter", min: 5 },
  { tier: "patron", min: 25 },
  { tier: "champion", min: 100 },
  { tier: "legend", min: 500 },
];

export default function Donate() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState({ presets: [5, 10, 25, 50, 100], min: 1, max: 10000 });
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [wall, setWall] = useState([]);
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/donations/config").then(({ data }) => { setCfg(data); setAmount(data.presets[1] || data.presets[0]); }).catch(() => {});
    api.get("/donations/recent", { params: { limit: 12 } }).then(({ data }) => setWall(data)).catch(() => {});
    if (params.get("cancelled")) toast.info("Donation cancelled — no charge was made.");
  }, [params]);

  const effective = custom ? parseFloat(custom) : amount;

  const donate = async () => {
    if (!user) { toast.info("Log in to donate and earn your supporter badge"); return nav("/login?next=/donate"); }
    const amt = custom ? parseFloat(custom) : amount;
    if (!amt || amt < cfg.min || amt > cfg.max) return toast.error(`Enter an amount between $${cfg.min} and $${cfg.max}`);
    setLoading(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount: amt, recurring, origin_url: window.location.origin,
        name: name.trim() || null, message: message.trim() || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 pb-20" data-testid="donate-page">
      <div className="text-center mb-8">
        <span className="inline-flex w-14 h-14 rounded-2xl bg-[#FF2A5F] items-center justify-center shadow-[0_8px_28px_rgba(255,42,95,0.45)] mb-4"><Heart size={26} className="text-white" fill="white" /></span>
        <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none">Support the archive</h1>
        <p className="text-[#9CA3AF] text-sm mt-3 max-w-md mx-auto">KinkVault is a community-run archive of queer erotic history. Your support keeps the servers running and the collection growing.</p>
      </div>

      <div className="bg-[#0F1117] border border-white/[0.08] rounded-2xl p-5 sm:p-7">
        <div className="flex gap-2 p-1 bg-black/40 rounded-xl mb-6" data-testid="donate-frequency">
          <button data-testid="donate-once" onClick={() => setRecurring(false)} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${!recurring ? "bg-[#FF2A5F] text-white" : "text-[#9CA3AF] hover:text-white"}`}><Sparkles size={15} /> One-time</button>
          <button data-testid="donate-monthly" onClick={() => setRecurring(true)} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${recurring ? "bg-[#FF2A5F] text-white" : "text-[#9CA3AF] hover:text-white"}`}><Repeat2 size={15} /> Monthly</button>
        </div>

        <div className="grid grid-cols-3 gap-2.5 mb-4" data-testid="donate-presets">
          {cfg.presets.map((p) => (
            <button key={p} data-testid={`donate-preset-${p}`} onClick={() => { setAmount(p); setCustom(""); }}
              className={`py-4 rounded-xl border text-lg font-bold transition-all ${!custom && amount === p ? "border-[#FF2A5F] bg-[#FF2A5F]/[0.12] text-white scale-[1.02]" : "border-white/[0.08] text-[#C9CAD1] hover:border-white/20"}`}>
              ${p}{recurring && <span className="text-xs font-normal text-[#9CA3AF]">/mo</span>}
            </button>
          ))}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-lg font-bold">$</span>
            <input data-testid="donate-custom" type="number" inputMode="decimal" min={cfg.min} max={cfg.max} placeholder="Other"
              value={custom} onChange={(e) => setCustom(e.target.value)}
              className="w-full h-full py-4 pl-7 pr-2 rounded-xl border border-white/[0.08] bg-black/40 text-white text-lg font-bold focus:border-[#FF2A5F] outline-none" />
          </div>
        </div>

        <input data-testid="donate-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Your name (optional)"
          className="w-full mb-3 px-4 py-3 rounded-xl border border-white/[0.08] bg-black/40 text-white text-sm focus:border-[#FF2A5F] outline-none" />
        <textarea data-testid="donate-message" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={280} rows={2} placeholder="Leave a message (optional)"
          className="w-full mb-5 px-4 py-3 rounded-xl border border-white/[0.08] bg-black/40 text-white text-sm focus:border-[#FF2A5F] outline-none resize-none" />

        <button data-testid="donate-submit" onClick={donate} disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-[#FF2A5F] hover:bg-[#e01e50] disabled:opacity-60 text-white font-heading uppercase tracking-wide text-sm transition-colors">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Heart size={16} fill="white" />}
          {loading ? "Redirecting…" : `Donate $${effective || 0}${recurring ? " / month" : ""}`}
        </button>
        <p className="text-center text-[11px] text-[#6B7280] mt-3">Secure payment via Stripe · card details never touch our servers</p>
      </div>

      <div className="mt-8 bg-[#0F1117] border border-white/[0.08] rounded-2xl p-5 sm:p-6" data-testid="donor-tiers">
        <h2 className="font-heading text-lg uppercase tracking-tight text-white mb-1">Supporter badges</h2>
        <p className="text-[#9CA3AF] text-xs mb-4">Earn a badge shown next to your name everywhere, based on your total lifetime support{user?.donated_total ? ` — you've given $${user.donated_total} so far` : ""}.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {TIER_ROWS.map(({ tier, min }) => {
            const earned = user && user.donor_tier && TIER_ROWS.findIndex((t) => t.tier === user.donor_tier) >= TIER_ROWS.findIndex((t) => t.tier === tier);
            return (
              <div key={tier} data-testid={`tier-${tier}`} className={`rounded-xl border p-3 text-center transition-colors ${earned ? "border-[#FF2A5F]/50 bg-white/[0.03]" : "border-white/[0.07]"}`}>
                <DonorBadge tier={tier} size={24} className="mb-1.5" />
                <p className="text-white text-sm font-bold" style={{ color: DONOR_TIERS[tier].color }}>{DONOR_TIERS[tier].label}</p>
                <p className="text-[#9CA3AF] text-xs">${min}+ total</p>
                {earned && <p className="text-[10px] uppercase font-bold text-[#00E676] mt-1">Earned</p>}
              </div>
            );
          })}
        </div>
      </div>

      {wall.length > 0 && (
        <div className="mt-10">
          <h2 className="font-heading text-lg uppercase tracking-tight text-white mb-4">Recent supporters</h2>
          <div className="flex flex-col gap-2.5" data-testid="donate-wall">
            {wall.map((d, i) => (
              <div key={i} className="bg-[#0F1117] border border-white/[0.06] rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                  <Heart size={13} className="text-[#FF2A5F]" fill="#FF2A5F" /> <span className="truncate">{d.name}</span>
                  <DonorBadge tier={d.donor_tier} size={13} />
                  {d.recurring && <span className="text-[10px] uppercase font-bold text-[#00E676] bg-[#00E676]/10 px-1.5 py-0.5 rounded">Monthly</span>}
                </p>
                {d.message && <p className="text-[13px] text-[#9CA3AF] mt-1 break-words">{d.message}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
